import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { SuperloginService } from '../superlogin/superlogin.service';
import { SIDEBAR_MENU, MenuItem } from './sidebar-menu.constant';

@Component({
  selector: 'app-left-navbar',
  templateUrl: './left-navbar.component.html',
  styleUrls: ['./left-navbar.component.scss'],
})
export class LeftNavbarComponent implements OnInit, OnDestroy {
    isLoggingOut: boolean = false; 

  private destroy$ = new Subject<void>();

  menuItems: MenuItem[] = [];
  currentUserRole: string | null = null;
  currentRoute: string = '';
  currentUser: any = null;
  userFullName: string = '';

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map((result) => result.matches),
      shareReplay(),
    );

  constructor(
    private breakpointObserver: BreakpointObserver,
    private authService: SuperloginService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initializeUserData();
    this.trackRouteChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeUserData(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.currentUserRole = this.authService.getUserRole();

    if (this.currentUserRole) {
      this.currentUserRole = this.currentUserRole.toUpperCase();
    }

    this.userFullName = this.getUserDisplayName();

    if (this.currentUserRole) {
      this.menuItems = this.filterMenuByRole(SIDEBAR_MENU, this.currentUserRole);
    }
  }

  private trackRouteChanges(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.currentRoute = event.urlAfterRedirects;
        }
      });
  }

  private getUserDisplayName(): string {
    if (!this.currentUser) return 'User';
    const firstName = this.currentUser.firstName || this.currentUser.first_name || '';
    const lastName = this.currentUser.lastName || this.currentUser.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || this.currentUser.email || this.currentUser.username || 'User';
  }

  filterMenuByRole(menu: MenuItem[], role: string): MenuItem[] {
    return menu
      .filter(item => this.hasAccess(item, role))
      .map(item => {
        if (item.children) {
          const filteredChildren = this.filterMenuByRole(item.children, role);
          if (filteredChildren.length > 0) {
            return { ...item, children: filteredChildren };
          }
          return null; 
        }
        return item;
      })
      .filter((item): item is MenuItem => item !== null);
  }

  getRoleDisplayName(role: string): string {
    const roleNames: { [key: string]: string } = {
      SYSADMIN: 'System Administrator',
      ACCOUNTANT: 'Accountant',
      MANAGEMENT: 'Management',
      REGISTRAR: 'Registrar',
      ADMIN: 'Administrator',
      QA: 'Quality Assurance',
      HOD: 'Head of Department',
      LECTURER: 'Lecturer',
      EXAM_OFFICER: 'Exam Officer'
    };
    const normalizedRole = role ? role.toUpperCase() : '';
    return roleNames[normalizedRole] || role;
  }

  getMenuItemCount(): number {
    return this.menuItems.length;
  }

  trackByMenuItem(index: number, item: MenuItem): string {
    return item.route || item.name || index.toString();
  }

  isActiveRoute(route: string): boolean {
    return !!route && this.currentRoute === route;
  }

  navigateTo(route: string): void {
    if (route) {
      this.router.navigate([route]);
    }
  }

  hasActiveChild(children?: MenuItem[]): boolean {
    if (!children) return false;
    return children.some(child => this.isActiveRoute(child.route));
  }

  refreshMenu(): void {
    this.menuItems = this.filterMenuByRole(SIDEBAR_MENU, this.currentUserRole || '');
  }

  logout(): void {
    // 1. Show the loader immediately
    this.isLoggingOut = true; 
    setTimeout(() => {
      this.authService.logout();
      this.router.navigate(['/super-login']);
      this.isLoggingOut = false; 
    }, 2000); 
  }

  hasAccess(item: MenuItem, role: string): boolean {
    if (!item.roles) return true;
    return item.roles.includes(role);
  }

  isDropdown(item: MenuItem): boolean {
    return !!item.children;
  }
}
